import { GeoJSONSource, type Map as MlMap, type ExpressionSpecification } from 'maplibre-gl'
import { hasTimsTonnage, resolveTimsTrip } from '../../lib/timsTrip'
import type { Breakdown, RouteProgress, TimsTrip, Vehicle, VehicleMeta } from '../../lib/types'
import type { VehicleFlag } from '../../lib/riskFlags'
import { vehicleState } from '../../lib/format'
import type { FenceIndex } from '../../lib/pointInPolygon'
import { locate } from '../../lib/pointInPolygon'
import { iconFor, type MarkerState } from './markers'

/**
 * The fleet itself: clustered in transit, badged at hubs.
 *
 * The split is the point. Proximity clustering alone gives you a bubble spanning the
 * Dhar plant and the highway beside it, whose count answers no question anyone asks.
 * A truck inside a geofence is counted by that geofence; a truck between hubs is
 * clustered with its neighbours. "Nine at the plant, three on the ghat" is a dispatch
 * report. "Twelve somewhere around here" is not.
 *
 * Vehicles inside a fence are still emitted as points -- they simply don't participate in
 * clustering -- so zooming into a plant shows the individual trucks, which is exactly the
 * "expanded view" the zoom-in is for.
 *
 * Each truck carries two facts beyond its position, and they use different channels:
 * STATE is the marker colour, LOAD is whether the cargo deck is filled or hollow. Keeping
 * load off the colour axis is what lets a laden truck also be idle, charging or broken
 * down -- which is exactly when you most need to know it is carrying 40 tonnes.
 */

export const SRC_TRANSIT = 'veh-transit'
export const SRC_ATHUB = 'veh-athub'
export const SRC_BADGES = 'hub-badges'
export const SRC_FLAGS = 'vehicle-flags'

export const LYR_CLUSTER = 'veh-cluster'
export const LYR_CLUSTER_COUNT = 'veh-cluster-count'
export const LYR_VEHICLE = 'veh-point'
export const LYR_VEHICLE_LABEL = 'veh-label'
export const LYR_BADGE = 'hub-badge'
export const LYR_BADGE_COUNT = 'hub-badge-count'
export const LYR_FLAG = 'veh-flag'
export const LYR_FLAG_LABEL = 'veh-flag-label'

/** Zoom at which hub badges hand over to individual trucks. */
const BADGE_MAX_ZOOM = 12.5

export interface VehicleFeatures {
  transit: GeoJSON.FeatureCollection<GeoJSON.Point>
  atHub: GeoJSON.FeatureCollection<GeoJSON.Point>
  badges: GeoJSON.FeatureCollection<GeoJSON.Point>
  flags: GeoJSON.FeatureCollection<GeoJSON.Point>
}

interface FenceCentroid { id: string; name: string; type: string; lon: number; lat: number }

export interface BuildVehicleOptions {
  /** Trip replay: keep truck icon visible at corridor zoom (skip hub badge routing). */
  replay?: boolean
}

/**
 * Is this unit running loaded?
 *
 * Taken from the assigned route rather than from a sensor, because there is no load cell
 * on these vehicles: the corridor runs laden Dhar -> Dhule and empty back, and the route
 * id is what encodes the direction. An unassigned vehicle reads as empty, which is the
 * conservative choice for the marker -- a hollow deck under-claims rather than showing
 * cargo that may not be there.
 */
/**
 * Load precedence, most authoritative first:
 *
 *  1. TIMS tonnage — logistics saying cargo is on board. A measured fact, not an inference.
 *  2. THE DIRECTION THE TRUCK IS ACTUALLY GOING, from live route progress. Toward Dhule is
 *     loaded; back toward Dhar is empty. This corridor runs one commodity one way.
 *  3. meta.load_state — the stream's own label, from the corridor-site rule.
 *  4. meta.route_id — the register's static assignment.
 *
 * WHY DIRECTION MOVED ABOVE meta.load_state. The marker was showing the wrong icon because
 * load_state was consulted first and is only written when a truck crosses a fence — so
 * every vehicle mid-corridor, which is most of them, carried whatever it was last told,
 * or nothing at all. Live route progress says which way the truck is pointing on every
 * fix, and on a one-way haul that IS the load state. It is also the same rule the reports
 * settled on (corridor_pos in reports_legs_from_geo.sql), so the map and the numbers now
 * answer the question the same way instead of two ways.
 */
export function isLaden(meta: VehicleMeta | undefined, trip?: TimsTrip,
                        progress?: RouteProgress): boolean {
  if (hasTimsTonnage(trip)) return true
  const live = progress?.route_id
  if (live) return /laden/i.test(live) && !/empty|unladen/i.test(live)
  if (meta?.load_state) return /^laden$/i.test(meta.load_state)
  return /laden/i.test(meta?.route_id ?? '') && !/empty|unladen/i.test(meta?.route_id ?? '')
}

/** Short label for live fleet lists — Loaded (Dhar→Dhule) vs Unloaded (Dhule→Dhar). */
export type LoadLabel = 'Loaded' | 'Unloaded'

export function loadLabel(meta: VehicleMeta | undefined, trip?: TimsTrip,
                          progress?: RouteProgress): LoadLabel | null {
  // NULL only when there is genuinely nothing to go on. Live direction now answers for
  // almost every moving truck, so a blank badge means the vehicle has no route position --
  // which is a real state worth showing as blank rather than guessing "Unloaded" about.
  if (hasTimsTonnage(trip)) return 'Loaded'
  if (progress?.route_id) return isLaden(meta, trip, progress) ? 'Loaded' : 'Unloaded'
  if (meta?.load_state) return isLaden(meta, trip, progress) ? 'Loaded' : 'Unloaded'
  if (!meta?.route_id) return null
  return isLaden(meta, trip, progress) ? 'Loaded' : 'Unloaded'
}

/**
 * Dead-reckon a moving truck between telemetry packets.
 *
 * Real devices (and the daybook) emit ~every 32 s. Without extrapolation the marker sits
 * still for half a minute then jumps — which reads as "not live". Cap the coast so a stale
 * feed cannot drive a truck into the next state.
 */
export function coastPosition(
  v: Pick<Vehicle, 'lat' | 'lon' | 'speed_kph' | 'heading_deg' | 'moving' | 'updated_ts' | 'last_geo_ts'>,
  nowMs = Date.now(),
  maxCoastS = 45,
): { lat: number; lon: number } | null {
  if (v.lat == null || v.lon == null) return null
  const speed = v.speed_kph ?? 0
  if (!v.moving || speed < 1) return { lat: v.lat, lon: v.lon }
  const origin = v.last_geo_ts || v.updated_ts
  if (!origin) return { lat: v.lat, lon: v.lon }
  const ageS = Math.max(0, (nowMs - origin) / 1000)
  if (ageS < 0.15) return { lat: v.lat, lon: v.lon }
  const coast = Math.min(ageS, maxCoastS)
  const distKm = (speed * coast) / 3600
  const rad = ((v.heading_deg ?? 0) * Math.PI) / 180
  const dLat = (distKm * Math.cos(rad)) / 111.32
  const cosLat = Math.max(0.2, Math.cos((v.lat * Math.PI) / 180))
  const dLon = (distKm * Math.sin(rad)) / (111.32 * cosLat)
  return { lat: v.lat + dLat, lon: v.lon + dLon }
}

/**
 * Nudge markers that share (almost) the same GPS so a hub pile-up is clickable.
 *
 * Daybook / yard GPS often pins every dwell to the same waypoint. MapLibre then builds a
 * cluster of 25 that expands to one stacked icon — count lies, click picks one plate.
 * Spread is display-only; telemetry coordinates are unchanged.
 */
export function spreadCoincident(
  features: GeoJSON.Feature<GeoJSON.Point>[],
  cellDeg = 0.00008,
): void {
  if (features.length < 2) return
  const groups = new Map<string, GeoJSON.Feature<GeoJSON.Point>[]>()
  for (const f of features) {
    const lon = f.geometry.coordinates[0]
    const lat = f.geometry.coordinates[1]
    if (lon == null || lat == null) continue
    const key = `${Math.round(lon / cellDeg)}:${Math.round(lat / cellDeg)}`
    const g = groups.get(key)
    if (g) g.push(f)
    else groups.set(key, [f])
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue
    let clon = 0
    let clat = 0
    for (const f of group) {
      clon += f.geometry.coordinates[0] ?? 0
      clat += f.geometry.coordinates[1] ?? 0
    }
    clon /= group.length
    clat /= group.length
    const n = group.length
    const radiusM = Math.min(90, 12 + 7 * Math.sqrt(n))
    const dLat = radiusM / 111_320
    const cos = Math.max(0.2, Math.cos((clat * Math.PI) / 180))
    const dLon = radiusM / (111_320 * cos)
    group.forEach((f, i) => {
      const a = (2 * Math.PI * i) / n - Math.PI / 2
      f.geometry = {
        type: 'Point',
        coordinates: [clon + dLon * Math.cos(a), clat + dLat * Math.sin(a)],
      }
    })
  }
}

/**
 * Split the roster into transit points, at-hub points and per-hub badge points.
 * Pure, and therefore the thing the unit tests exercise.
 */
export function buildVehicleFeatures(
  vehicles: readonly Vehicle[],
  fences: readonly FenceIndex[],
  centroids: readonly FenceCentroid[],
  now = Date.now(),
  meta?: ReadonlyMap<string, VehicleMeta>,
  breakdowns?: ReadonlyMap<string, Breakdown>,
  riskFlags?: ReadonlyMap<string, VehicleFlag>,
  timsTrips?: ReadonlyMap<string, TimsTrip>,
  options?: BuildVehicleOptions,
  /** Live route position per vehicle. Carries the DIRECTION, which on a one-way haul is
   *  the load state -- see isLaden. */
  progress?: ReadonlyMap<string, RouteProgress>,
): VehicleFeatures {
  const replay = options?.replay ?? false
  const transit: GeoJSON.Feature<GeoJSON.Point>[] = []
  const atHub: GeoJSON.Feature<GeoJSON.Point>[] = []
  const perFence = new Map<string, number>()

  for (const v of vehicles) {
    if (v.lat == null || v.lon == null) continue
    // A marked breakdown OVERRIDES every telemetry-derived state. A truck with a snapped
    // spring reports a healthy pack and a speed of zero, so the feed will happily call it
    // "idle" forever; the human who went and looked outranks it.
    const down = breakdowns?.get(v.vehicle_id)
    const state: MarkerState = down ? 'down' : vehicleState(v, now)
    const trip = timsTrips ? resolveTimsTrip(v.vehicle_id, timsTrips) : undefined
    const laden = isLaden(meta?.get(v.vehicle_id), trip, progress?.get(v.vehicle_id))
    const flag = riskFlags?.get(v.vehicle_id)
    // Fence membership uses the last reported fix (not coasted) so a truck does not
    // "enter" a hub in the UI before GPS does.
    const fence = fences.length ? locate(v.lon, v.lat, fences) : null
    const coasted = coastPosition(v, now) ?? { lat: v.lat, lon: v.lon }
    const feature: GeoJSON.Feature<GeoJSON.Point> = {
      type: 'Feature',
      id: v.vehicle_id,
      geometry: { type: 'Point', coordinates: [coasted.lon, coasted.lat] },
      properties: {
        vehicle_id: v.vehicle_id,
        state,
        laden,
        icon: iconFor(state, laden),
        down_reason: down?.category ?? null,
        down_severity: down?.severity ?? null,
        // Route-matched bearing over the device's own raw GPS course: heading_deg is the
        // vehicle's instantaneous compass reading, which is noisy at low speed and on
        // bends (see geo.cross_track's own comment) -- rotating the icon by it points the
        // truck somewhere other than along the road it is actually on. route_bearing_deg
        // is the direction of the matched route segment itself, so the icon faces the way
        // the corridor runs. Falls back to raw heading only off-route / no route match.
        heading: progress?.get(v.vehicle_id)?.route_bearing_deg ?? v.heading_deg ?? 0,
        speed_kph: v.speed_kph,
        soc_pct: v.soc_pct,
        fence_id: fence?.id ?? null,
        fence_name: fence?.name ?? null,
        flagged: Boolean(flag),
        flag_kind: flag?.kind ?? null,
        flag_label: flag?.label ?? null,
        flag_detail: flag?.detail ?? null,
        flag_severity: flag?.severity ?? null,
      },
    }
    if (fence && !replay) {
      atHub.push(feature)
      perFence.set(fence.id, (perFence.get(fence.id) ?? 0) + 1)
    } else {
      transit.push(feature)
    }
  }

  // Spread stacks so hub badges that say "25" actually resolve to 25 clickable trucks.
  spreadCoincident(transit)
  if (!replay) spreadCoincident(atHub)

  const badges: GeoJSON.Feature<GeoJSON.Point>[] = []
  if (!replay) {
    for (const c of centroids) {
      const count = perFence.get(c.id) ?? 0
      if (!count) continue
      badges.push({
        type: 'Feature',
        id: c.id,
        geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
        properties: { geofence_id: c.id, name: c.name, type: c.type, count },
      })
    }
  }

  // Flag pins sit on the (possibly spread) truck coordinates so click and marker agree.
  const flagFeats: GeoJSON.Feature<GeoJSON.Point>[] = []
  for (const f of [...transit, ...atHub]) {
    if (!f.properties?.['flagged']) continue
    flagFeats.push({
      type: 'Feature',
      id: `flag:${f.properties['vehicle_id']}`,
      geometry: f.geometry,
      properties: {
        vehicle_id: f.properties['vehicle_id'],
        flag_kind: f.properties['flag_kind'],
        flag_label: f.properties['flag_label'],
        flag_detail: f.properties['flag_detail'],
        flag_severity: f.properties['flag_severity'],
        speed_kph: f.properties['speed_kph'],
        soc_pct: f.properties['soc_pct'],
      },
    })
  }

  return {
    transit: { type: 'FeatureCollection', features: transit },
    atHub: { type: 'FeatureCollection', features: atHub },
    badges: { type: 'FeatureCollection', features: badges },
    flags: { type: 'FeatureCollection', features: flagFeats },
  }
}

/**
 * The icon id is computed in buildVehicleFeatures and read straight off the feature.
 *
 * A `match` expression would need ten arms now (five states x laden/empty) and twenty the
 * next time a state is added -- a combinatorial table maintained in a place the type
 * checker cannot see. One property, resolved once in TypeScript where iconFor() is
 * checked against MarkerState, does not have that problem.
 */
const stateIcon: ExpressionSpecification =
  ['coalesce', ['get', 'icon'], iconFor('idle', false)] as unknown as ExpressionSpecification

const empty: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features: [] }

export function addVehicleLayers(map: MlMap): void {
  if (!map.getSource(SRC_TRANSIT)) {
    map.addSource(SRC_TRANSIT, {
      type: 'geojson',
      data: empty,
      cluster: true,
      clusterRadius: 46,
      // Past this zoom every truck stands alone -- the "expanded view".
      clusterMaxZoom: 12,
    })
  }
  if (!map.getSource(SRC_ATHUB)) {
    map.addSource(SRC_ATHUB, { type: 'geojson', data: empty })
  }
  if (!map.getSource(SRC_BADGES)) {
    map.addSource(SRC_BADGES, { type: 'geojson', data: empty })
  }
  if (!map.getSource(SRC_FLAGS)) {
    map.addSource(SRC_FLAGS, { type: 'geojson', data: empty })
  }

  // ------------------------------------------------------------------ transit clusters
  if (!map.getLayer(LYR_CLUSTER)) {
    map.addLayer({
      id: LYR_CLUSTER,
      type: 'circle',
      source: SRC_TRANSIT,
      filter: ['has', 'point_count'],
      paint: {
        // White disc, ink figure, coloured ring. On a pale basemap a dark cluster reads as
        // a hole punched in the map; a white one reads as a chip laid on top of it, which
        // is what it is.
        'circle-color': '#FFFFFF',
        'circle-stroke-color': '#0F172A',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.96,
        'circle-radius': [
          'interpolate', ['linear'], ['get', 'point_count'],
          2, 15, 10, 21, 40, 30,
        ],
      },
    })
    map.addLayer({
      id: LYR_CLUSTER_COUNT,
      type: 'symbol',
      source: SRC_TRANSIT,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Medium'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#0F172A' },
    })
  }

  // ------------------------------------------------------------------- individual trucks
  // One layer over both sources would be simpler; two exists so at-hub trucks stay
  // visible at low zoom underneath their badge without joining a transit cluster.
  for (const [id, src] of [[LYR_VEHICLE, SRC_TRANSIT], ['veh-point-hub', SRC_ATHUB]] as const) {
    if (map.getLayer(id)) continue
    map.addLayer({
      id,
      type: 'symbol',
      source: src,
      ...(src === SRC_TRANSIT ? { filter: ['!', ['has', 'point_count']] } : {}),
      ...(src === SRC_ATHUB ? { minzoom: BADGE_MAX_ZOOM } : {}),
      layout: {
        'icon-image': stateIcon,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-rotate': ['get', 'heading'],
        'icon-rotation-alignment': 'map',
        // Tuned against the 38x66 source in markers.ts, not carried over from the old
        // 30x40 one: at corridor zoom a truck is a token and must not swamp the road it
        // is on, and by z14 the dispatcher is looking at one hub and wants the silhouette
        // -- axles, cab, load bands -- actually legible.
        'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 12, 0.78, 14, 0.95, 16, 1.2],
      },
      paint: {
        'icon-opacity': ['case', ['==', ['get', 'state'], 'stale'], 0.55, 1],
      },
    })
  }

  if (!map.getLayer(LYR_VEHICLE_LABEL)) {
    map.addLayer({
      id: LYR_VEHICLE_LABEL,
      type: 'symbol',
      source: SRC_TRANSIT,
      filter: ['!', ['has', 'point_count']],
      minzoom: 12.5,
      layout: {
        'text-field': ['get', 'vehicle_id'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 10,
        // Clear of the marker's tail. Offsets are in ems of text-size, so 3 = 30 px --
        // roughly half the icon's on-screen height at the zooms where the plate shows.
        // Left at 1.3 the plate printed across the cargo deck.
        'text-offset': [0, 3],
        'text-anchor': 'top',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#0F172A',
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.6,
      },
    })
  }

  // ---------------------------------------------------------------- risk flags (clickable)
  // A chip above the truck: SPD / HRSH / SoC / !. Click opens the detail popup.
  if (!map.getLayer(LYR_FLAG)) {
    map.addLayer({
      id: LYR_FLAG,
      type: 'circle',
      source: SRC_FLAGS,
      // A small dot, not a badge.
      //
      // This was an 11 px filled disc floating 22 px above every flagged truck with the
      // label crammed inside it, drawn with text-allow-overlap and ignore-placement -- so
      // at corridor zoom the discs stacked into a wall of red and amber and the map stopped
      // being readable. The flag is an annotation on a vehicle, and it should not outweigh
      // the vehicle.
      //
      // The dot now just anchors the label's colour; the label carries the meaning.
      minzoom: 9.5,
      paint: {
        'circle-radius': 3.2,
        'circle-color': [
          'match', ['get', 'flag_severity'],
          'critical', '#9F1239',
          '#B45309',
        ],
        'circle-stroke-color': '#FFFFFF',
        'circle-stroke-width': 1,
        // Above the nose, clear of the taller marker. Viewport-anchored, so it stays put
        // as the truck rotates with its heading.
        'circle-translate': [0, -30],
        'circle-translate-anchor': 'viewport',
      },
    })
    map.addLayer({
      id: LYR_FLAG_LABEL,
      type: 'symbol',
      source: SRC_FLAGS,
      minzoom: 9.5,
      layout: {
        'text-field': ['get', 'flag_label'],
        'text-font': ['Noto Sans Medium'],
        'text-size': 10,
        // COLLISION-AWARE, deliberately. The previous version forced every label to draw,
        // which is why they overlapped into noise. Letting MapLibre drop the ones that
        // collide means the labels you can read are the ones worth reading, and zooming
        // in reveals the rest -- which is how a map is supposed to degrade.
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-optional': true,
        'text-offset': [0, -3.3],
        'text-letter-spacing': 0.04,
      },
      paint: {
        // Coloured text on a white halo rather than white text on a coloured disc: it
        // reads at 10 px against road, satellite or terrain without a filled shape
        // competing with the truck marker underneath it.
        'text-color': [
          'match', ['get', 'flag_severity'],
          'critical', '#9F1239',
          '#B45309',
        ],
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 1.4,
      },
    })
  }

  // ------------------------------------------------------------------------ hub badges
  if (!map.getLayer(LYR_BADGE)) {
    map.addLayer({
      id: LYR_BADGE,
      type: 'circle',
      source: SRC_BADGES,
      maxzoom: BADGE_MAX_ZOOM,
      paint: {
        'circle-color': '#FFFFFF',
        // Amber ring, not an amber fill: a hub badge is a count, not a warning, and it
        // must not read at a glance as an alert.
        'circle-stroke-color': '#B45309',
        'circle-stroke-width': 1.8,
        'circle-opacity': 0.96,
        'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 14, 10, 20, 40, 28],
      },
    })
    map.addLayer({
      id: LYR_BADGE_COUNT,
      type: 'symbol',
      source: SRC_BADGES,
      maxzoom: BADGE_MAX_ZOOM,
      layout: {
        'text-field': ['to-string', ['get', 'count']],
        'text-font': ['Noto Sans Medium'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#B45309' },
    })
  }
}

export function setVehicleData(map: MlMap, f: VehicleFeatures): void {
  for (const [id, data] of [
    [SRC_TRANSIT, f.transit], [SRC_ATHUB, f.atHub], [SRC_BADGES, f.badges],
    [SRC_FLAGS, f.flags],
  ] as const) {
    const src = map.getSource(id)
    if (src instanceof GeoJSONSource) src.setData(data)
  }
}
